import MigrationItemViewDTO from '@src/adapters/dto/migration/MigrationItemViewDTO';
import { Box, Checkbox, Fade, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useEffect, useRef, useState } from 'react';
import CardItemDetails from './CardItemDetails';
import EditIcon from '@mui/icons-material/Edit';
import MigrationItemUpdateDTO from '@src/adapters/dto/migration/MigrationItemUpdateDTO';
import { MigrationStatus } from '@src/constants/MigrationStatus';

interface Props {
  item: MigrationItemViewDTO;
  highlightedId?: string;
  lockView: Boolean;
  status: MigrationStatus;
  isOwner: Boolean;
  itemUpdated(item: MigrationItemUpdateDTO): void;
  deleteItem(id: string): void;
  highlightItem(suggestionId?: string): void;
  handleApprovalUpdate(
    itemId: string,
    suggestionId: string | undefined,
    approved: boolean
  ): void;
}

function CardItem({
  item,
  highlightedId,
  lockView,
  status,
  isOwner,
  itemUpdated,
  deleteItem,
  highlightItem,
  handleApprovalUpdate,
}: Props) {
  const [highlighted, setHighlighted] = useState(false);

  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const iconColor = highlighted ? '#2196F3' : '#000';

  const note = useRef(null);
  const [hovered, setHovered] = useState(false);
  const approvalChecked = !!item?.approval?.userId;

  useEffect(() => {
    if (item.suggestion) {
      setHighlighted(highlightedId === item.suggestion.id);
    }
  }, [highlightedId, item.suggestion]);

  const handleToggleVisibility = () => {
    if (item.suggestion) {
      highlightItem(item.suggestion.id);
    } else {
      highlightItem(undefined);
    }
  };

  const handleItemDetailsClosed = (item: MigrationItemUpdateDTO) => {
    setEditDetailsOpen(false);
    itemUpdated(item);
  };

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const suggestionId = item.suggestion ? item.suggestion.id : undefined;
    handleApprovalUpdate(item.id, suggestionId, event.target.checked);
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          padding: theme.spacing(1),
          paddingLeft: theme.spacing(3),
          borderTop: '1px solid #E6E7E9',
          boxSizing: 'content-box',
          justifyContent: 'space-between',
        })}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {status === MigrationStatus.InApproval && !isOwner && (
              <Checkbox checked={approvalChecked} onChange={handleCheck} />
            )}
            <Typography variant="body2" color="theme.pallete.warning.main">
              {`${item.inventory.name}${item.isLoan ? ' (Loan)' : ''}`}
            </Typography>
            <Box
              sx={{
                maxWidth: '150px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                })}
                ref={note}>
                {item.note}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'left',
            gap: theme.spacing(1),
          })}>
          <Box
            sx={{
              width: '80px',
            }}>
            <Box>
              {item.qtyOpen > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ width: theme.spacing(8) })}>
                    Open:
                  </Typography>
                  <Typography variant="subtitle2">{item.qtyOpen}</Typography>
                </Box>
              )}
              {item.qtyOnHand > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ width: theme.spacing(8) })}>
                    On hand:
                  </Typography>
                  <Typography variant="subtitle2">{item.qtyOnHand}</Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Fade in={hovered || highlighted} timeout={300}>
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                width: theme.spacing(3),
              })}>
              {!lockView && item.suggestion && (
                <VisibilityIcon
                  onClick={handleToggleVisibility}
                  sx={{
                    color: iconColor,
                    cursor: 'pointer',
                  }}
                />
              )}
              {lockView && item.suggestion && (
                <VisibilityOffIcon sx={{ color: iconColor }} />
              )}
            </Box>
          </Fade>
          <Fade in={hovered} timeout={300}>
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                width: theme.spacing(3),
              })}>
              <EditIcon
                onClick={() => setEditDetailsOpen(true)}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Fade>
        </Box>
      </Box>
      <CardItemDetails
        item={item}
        open={editDetailsOpen}
        status={status}
        anchorEl={note.current}
        cancel={() => setEditDetailsOpen(false)}
        close={handleItemDetailsClosed}
        deleteItem={deleteItem}
      />
    </>
  );
}

export default CardItem;
